import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { InfoIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components';
import { TextField } from '@/components/TextField';
import { Button } from '@/components/ui/Button';
import { Permission, usePermissions } from '@/hooks/usePermissions';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { AdminSettingsService } from '@/services/engine/admin-settings-service';
import type {
    OrganizationConfiguration,
    UpdateOrganizationConfigurationRequest,
} from '@/types/admin-settings';
import { getApiErrorMessage } from '@/utils/error-messages';
import {
    revalidateFeatureFlags,
    useConfiguration,
} from '../hooks/useConfiguration';
import { useLicenseStatus } from '../hooks/useLicenseStatus';
import { FeatureFlagRow } from './FeatureFlagRow';

interface ConfigurationFormValues {
    maxUsers: string;
    maxCameras: string;
    maxAlertUnits: string;
    hdmiEnabled: boolean;
    videoFilesEnabled: boolean;
    liveCamerasEnabled: boolean;
    alertsEnabled: boolean;
}

type FlagKey =
    | 'videoFilesEnabled'
    | 'liveCamerasEnabled'
    | 'hdmiEnabled'
    | 'alertsEnabled';

const FLAG_FIELDS: { key: FlagKey; labelKey: string }[] = [
    { key: 'videoFilesEnabled', labelKey: 'configuration.flags.videoFiles' },
    { key: 'liveCamerasEnabled', labelKey: 'configuration.flags.liveCameras' },
    { key: 'hdmiEnabled', labelKey: 'configuration.flags.hdmi' },
    { key: 'alertsEnabled', labelKey: 'configuration.flags.alerts' },
];

const SOURCE_FLAG_KEYS: FlagKey[] = [
    'videoFilesEnabled',
    'liveCamerasEnabled',
    'hdmiEnabled',
];

function licensedMaximum(
    licensed: boolean | undefined,
    maximum: number | null | undefined,
): number | null {
    if (!licensed || maximum == null) return null;
    return maximum;
}

function exceedsMaximum(value: string, maximum: number | null): boolean {
    if (maximum === null || !/^[1-9]\d*$/.test(value.trim())) return false;
    return Number.parseInt(value, 10) > maximum;
}

function getDefaultValues(
    configuration: OrganizationConfiguration,
): ConfigurationFormValues {
    return {
        maxUsers: String(configuration.maxUsers.value),
        maxCameras: String(configuration.maxCameras.value),
        maxAlertUnits:
            configuration.maxAlertUnits === null
                ? ''
                : String(configuration.maxAlertUnits),
        hdmiEnabled: configuration.hdmiEnabled.value,
        videoFilesEnabled: configuration.videoFilesEnabled.value,
        liveCamerasEnabled: configuration.liveCamerasEnabled.value,
        alertsEnabled: configuration.alertsEnabled.value,
    };
}

export default function ConfigurationTab() {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const { has } = usePermissions();
    const { configuration, isLoading, error, mutate } = useConfiguration();
    const { license } = useLicenseStatus();
    const userMaximum = licensedMaximum(license?.licensed, license?.maxUsers);
    const cameraMaximum = licensedMaximum(
        license?.licensed,
        license?.maxCameras,
    );
    const alertUnitMaximum = licensedMaximum(
        license?.licensed,
        license?.maxAlertUnits,
    );
    const canEdit = has(Permission.ORGANIZATION_CONFIGURATION_EDIT);
    const [isEditing, setIsEditing] = useState(false);

    const {
        register,
        reset,
        handleSubmit,
        watch,
        setValue,
        formState: { isDirty, isSubmitting },
    } = useForm<ConfigurationFormValues>({
        mode: 'onChange',
        defaultValues: {
            maxUsers: '',
            maxCameras: '',
            maxAlertUnits: '',
            hdmiEnabled: false,
            videoFilesEnabled: true,
            liveCamerasEnabled: true,
            alertsEnabled: true,
        },
    });

    useEffect(() => {
        if (!configuration || isEditing) return;
        reset(getDefaultValues(configuration));
    }, [configuration, isEditing, reset]);

    const formValues = watch();

    const isPositiveInt = (value: string): boolean =>
        /^[1-9]\d*$/.test(value.trim());

    const atLeastOneSourceEnabled = SOURCE_FLAG_KEYS.some(
        (key) => formValues[key],
    );

    const alertsEnabled = formValues.alertsEnabled;
    const usersAboveMaximum = exceedsMaximum(formValues.maxUsers, userMaximum);
    const camerasAboveMaximum = exceedsMaximum(
        formValues.maxCameras,
        cameraMaximum,
    );
    const alertUnitsAboveMaximum =
        alertsEnabled &&
        exceedsMaximum(formValues.maxAlertUnits, alertUnitMaximum);
    const alertUnitsValid =
        !alertsEnabled ||
        ((formValues.maxAlertUnits.trim() === '' ||
            isPositiveInt(formValues.maxAlertUnits)) &&
            !alertUnitsAboveMaximum);

    const limitError = (value: string, maximum: number | null) => {
        if (value.length > 0 && !isPositiveInt(value)) {
            return t('configuration.validation.positive');
        }
        if (exceedsMaximum(value, maximum)) {
            return t('configuration.validation.aboveMax', { max: maximum });
        }
        return undefined;
    };

    const isFormValid = useMemo(() => {
        return (
            isPositiveInt(formValues.maxUsers) &&
            !usersAboveMaximum &&
            isPositiveInt(formValues.maxCameras) &&
            !camerasAboveMaximum &&
            alertUnitsValid &&
            atLeastOneSourceEnabled
        );
    }, [
        formValues.maxUsers,
        formValues.maxCameras,
        usersAboveMaximum,
        camerasAboveMaximum,
        alertUnitsValid,
        atLeastOneSourceEnabled,
    ]);

    const saveDisabled = !isDirty || !isFormValid || isSubmitting;

    const onCancel = () => {
        if (configuration) {
            reset(getDefaultValues(configuration));
        }
        setIsEditing(false);
    };

    const onSubmit = handleSubmit(async (values) => {
        const payload: UpdateOrganizationConfigurationRequest = {
            maxUsers: Number.parseInt(values.maxUsers, 10),
            maxCameras: Number.parseInt(values.maxCameras, 10),
            hdmiEnabled: values.hdmiEnabled,
            videoFilesEnabled: values.videoFilesEnabled,
            liveCamerasEnabled: values.liveCamerasEnabled,
            alertsEnabled: values.alertsEnabled,
        };
        if (values.alertsEnabled && configuration) {
            const trimmedAlertUnits = values.maxAlertUnits.trim();
            if (trimmedAlertUnits === '') {
                if (configuration.maxAlertUnits !== null) {
                    payload.maxAlertUnits = null;
                }
            } else if (
                Number.parseInt(trimmedAlertUnits, 10) !==
                configuration.maxAlertUnits
            ) {
                payload.maxAlertUnits = Number.parseInt(trimmedAlertUnits, 10);
            }
        }

        try {
            const updated =
                await AdminSettingsService.updateOrganizationConfiguration(
                    payload,
                );
            await mutate(updated, { revalidate: false });
            await revalidateFeatureFlags();
            reset(getDefaultValues(updated));
            setIsEditing(false);
            toast.success(t('configuration.toast.updateSuccess'));
        } catch (submitError) {
            toast.error(
                getApiErrorMessage(
                    submitError,
                    t('configuration.toast.updateError'),
                ),
            );
        }
    });

    if (isLoading) {
        return <div className="text-sm text-grey-300 pt-14">Loading...</div>;
    }

    if (error || !configuration) {
        return (
            <div className="border border-danger-300 rounded-[5px] p-4 text-danger-200 text-sm">
                {t('configuration.toast.updateError')}
            </div>
        );
    }

    return (
        <div
            className="flex flex-col gap-6 pt-14 pb-24"
            data-testid="configuration-tab"
        >
            <div className="flex items-center justify-between gap-4">
                <h2 className="text-lg font-semibold text-white-100">
                    {t('configuration.title')}
                </h2>
                {canEdit ? (
                    isEditing ? (
                        <div className="flex items-center gap-2 shrink-0">
                            <Button
                                type="submit"
                                form="organization-configuration-form"
                                disabled={saveDisabled}
                                loading={isSubmitting}
                            >
                                {t('configuration.actions.save')}
                            </Button>
                            <Button
                                type="button"
                                variant="tertiary"
                                onClick={onCancel}
                            >
                                {t('configuration.actions.cancel')}
                            </Button>
                        </div>
                    ) : (
                        <Button
                            data-testid="configuration-edit"
                            variant="tertiary"
                            onClick={() => {
                                reset(getDefaultValues(configuration));
                                setIsEditing(true);
                            }}
                        >
                            {t('configuration.actions.edit')}
                        </Button>
                    )
                ) : null}
            </div>

            {!canEdit ? (
                <div className="flex items-start gap-2 p-3 rounded-md border border-grey-600 bg-grey-700/40">
                    <InfoIcon className="size-4 text-grey-300 shrink-0 mt-0.5" />
                    <p className="text-sm text-grey-200">
                        {t('configuration.viewOnly.notice')}
                    </p>
                </div>
            ) : null}

            <form
                id="organization-configuration-form"
                className="flex flex-col gap-3"
                onSubmit={onSubmit}
            >
                <h3 className="text-base font-medium text-white-100">
                    {t('configuration.limitations.title')}
                </h3>

                <div className="border border-grey-600 rounded-[10px] p-4 flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                        <span className="text-sm text-grey-200">
                            {t('configuration.maxUsers.label')}
                        </span>
                        <span className="text-xs text-grey-300">
                            {t('configuration.usage.label', {
                                current: configuration.currentUsers,
                            })}
                        </span>
                    </div>
                    {isEditing ? (
                        <TextField
                            type="number"
                            min={1}
                            max={userMaximum ?? undefined}
                            aria-label={t('configuration.maxUsers.label')}
                            className="w-24"
                            error={limitError(formValues.maxUsers, userMaximum)}
                            {...register('maxUsers')}
                        />
                    ) : (
                        <span className="text-sm text-white-100">
                            {configuration.maxUsers.value}
                        </span>
                    )}
                </div>

                <div className="border border-grey-600 rounded-[10px] p-4 flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                        <span className="text-sm text-grey-200">
                            {t('configuration.maxCameras.label')}
                        </span>
                        <span className="text-xs text-grey-300">
                            {t('configuration.usage.label', {
                                current: configuration.currentCameras,
                            })}
                        </span>
                    </div>
                    {isEditing ? (
                        <TextField
                            type="number"
                            min={1}
                            max={cameraMaximum ?? undefined}
                            aria-label={t('configuration.maxCameras.label')}
                            className="w-24"
                            error={limitError(
                                formValues.maxCameras,
                                cameraMaximum,
                            )}
                            {...register('maxCameras')}
                        />
                    ) : (
                        <span className="text-sm text-white-100">
                            {configuration.maxCameras.value}
                        </span>
                    )}
                </div>

                <div
                    className={`border border-grey-600 rounded-[10px] p-4 flex items-center justify-between ${
                        alertsEnabled ? '' : 'opacity-40'
                    }`}
                >
                    <div className="flex flex-col gap-1">
                        <span className="text-sm text-grey-200">
                            {t('configuration.maxAlertUnits.label')}
                        </span>
                        <span className="text-xs text-grey-300">
                            {t('configuration.usage.label', {
                                current: configuration.currentAlertUnits,
                            })}
                        </span>
                    </div>
                    {isEditing ? (
                        <TextField
                            type="number"
                            min={1}
                            max={alertUnitMaximum ?? undefined}
                            disabled={!alertsEnabled}
                            aria-label={t('configuration.maxAlertUnits.label')}
                            className="w-24"
                            placeholder={t(
                                'configuration.maxAlertUnits.unlimited',
                            )}
                            error={
                                alertsEnabled
                                    ? limitError(
                                          formValues.maxAlertUnits,
                                          alertUnitMaximum,
                                      )
                                    : undefined
                            }
                            {...register('maxAlertUnits')}
                        />
                    ) : (
                        <span className="text-sm text-white-100">
                            {configuration.maxAlertUnits ??
                                t('configuration.maxAlertUnits.unlimited')}
                        </span>
                    )}
                </div>

                <h3 className="text-base font-medium text-white-100 mt-4">
                    {t('configuration.flags.title')}
                </h3>

                {FLAG_FIELDS.map(({ key, labelKey }) => (
                    <FeatureFlagRow
                        key={key}
                        label={t(`${labelKey}.label`)}
                        description={t(`${labelKey}.description`)}
                        checked={formValues[key]}
                        disabled={!isEditing}
                        onCheckedChange={(value) =>
                            setValue(key, value, { shouldDirty: true })
                        }
                    />
                ))}

                {isEditing && !atLeastOneSourceEnabled ? (
                    <p className="text-sm text-danger-200">
                        {t('configuration.validation.atLeastOneSource')}
                    </p>
                ) : null}
            </form>
        </div>
    );
}
